import {useMemo} from "react";
import {mergeDemoActivity} from "../../userStats/demoData";

// Split seconds into whole hours + minutes for the headline
// (plain-language: "1 hour 20 minutes", not "1.3 hours").
export const toHoursMinutes = (seconds) => {
  const totalMinutes = Math.round((Number(seconds) || 0) / 60);
  return {hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60};
};

const VideoStats = ({activity, watchHistory}) => {
  const videoSeconds = useMemo(() => {
    // Current calendar month only (e.g. "2026-06"); daily keys are YYYY-MM-DD.
    const now = new Date();
    const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    // Demo build only: overlay the deterministic demo activity for DISPLAY so
    // the landing isn't empty. This does NOT touch the synced state (useSync
    // reads the real activity), so no mock data is ever written to the backend.
    const source = process.env.REACT_APP_DEMO_ACCOUNT_PREFIX
      ? mergeDemoActivity(activity)
      : activity || {};
    let video = 0;
    Object.entries(source).forEach(([date, stats]) => {
      if (date.startsWith(monthPrefix)) {
        video += stats.totalVideoSeconds || 0;
      }
    });
    return video;
  }, [activity]);

  // Demo mode (same flag as the admin mock filter): the headline includes
  // seconds so watch time visibly ticks during a live presentation.
  const demoMode = Boolean(process.env.REACT_APP_DEMO_ACCOUNT_PREFIX);
  const parts = [];
  if (demoMode) {
    const total = Math.floor(Number(videoSeconds) || 0);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    if (hours > 0)
      parts.push({value: hours, unit: hours === 1 ? "hour" : "hours"});
    if (minutes > 0 || hours > 0)
      parts.push({value: minutes, unit: minutes === 1 ? "minute" : "minutes"});
    parts.push({value: secs, unit: secs === 1 ? "second" : "seconds"});
  } else {
    const {hours, minutes} = toHoursMinutes(videoSeconds);
    if (hours > 0)
      parts.push({value: hours, unit: hours === 1 ? "hour" : "hours"});
    if (minutes > 0 || hours === 0)
      parts.push({value: minutes, unit: minutes === 1 ? "minute" : "minutes"});
  }

  return (
    <section className="mb-4">
      <p className="text-lg sm:text-xl font-semibold text-[#262626] mb-3 max-w-2xl">
        Watch job training videos to meet Idaho&rsquo;s work requirements and
        keep your SNAP or TAFI benefits.
      </p>
      <div className="bg-[#F8FAFC] border border-slate-100 rounded-2xl shadow-sm p-4">
        <p className="text-sm font-semibold text-[#262626] tracking-tight">
          Your progress this month
        </p>
        <p className="mt-1 leading-none">
          {parts.map(({value, unit}, i) => (
            <span key={unit}>
              {i > 0 && " "}
              <span className="text-3xl font-bold text-[#001489]">
                {value}
              </span>{" "}
              <span className="text-lg font-bold text-[#001489]">{unit}</span>
            </span>
          ))}
        </p>
        <p className="text-xs font-medium text-[#262626]/60 mt-1">
          of videos watched this month
        </p>
      </div>
    </section>
  );
};

export default VideoStats;
